import { Card, CardActionArea, CardContent, CardMedia, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import type { GoalListItem } from '../../api/goals-api';

interface InitiativeCardProps {
  goal: GoalListItem;
  popularitySummary?: string;
}

const InitiativeCardRoot = styled(Card)(({ theme }) => {
  const palette = theme.vars?.palette ?? theme.palette;
  const surface = palette.surface ?? {
    panelBorder: palette.divider,
    shadow: 'none',
  };

  return {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${surface.panelBorder}`,
    background: palette.background.paper,
    boxShadow: surface.shadow,
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
    },
  };
});

const InitiativeCardActionArea = styled(CardActionArea)({
  display: 'flex',
  height: '100%',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
}) as typeof CardActionArea;

const CoverMedia = styled(CardMedia)(({ theme }) => ({
  height: 128,
  backgroundColor:
    theme.vars?.palette.surface.soft ??
    theme.palette.surface?.soft ??
    theme.palette.background.default,
})) as typeof CardMedia;

const InitiativeContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: theme.spacing(1),
  cursor: 'default',
}));

const InitiativeTitle = styled(Typography)({
  fontWeight: 700,
});

const InitiativeDescription = styled(Typography)({
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
});

function getCoverSeed(value: string) {
  return value.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
}

function escapeSvgText(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function createCoverDataUri({
  accent,
  background,
  label,
  muted,
  seed,
}: {
  accent: string;
  background: string;
  label: string;
  muted: string;
  seed: number;
}) {
  const markerX = 48 + (seed % 48);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 160" role="img">
      <rect width="320" height="160" fill="${background}" />
      <rect x="20" y="20" width="150" height="18" rx="8" fill="${muted}" opacity="0.72" />
      <rect x="20" y="52" width="210" height="14" rx="7" fill="${muted}" opacity="0.42" />
      <rect x="20" y="80" width="118" height="14" rx="7" fill="${muted}" opacity="0.34" />
      <circle cx="${markerX}" cy="122" r="26" fill="${accent}" opacity="0.92" />
      <text x="${markerX}" y="130" text-anchor="middle" font-family="Inter, Segoe UI, sans-serif" font-size="20" font-weight="700" fill="white">${escapeSvgText(label)}</text>
    </svg>
  `;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function InitiativeCard({
  goal,
  popularitySummary,
}: InitiativeCardProps) {
  const theme = useTheme();
  const palette = theme.vars?.palette ?? theme.palette;
  const coverSeed = getCoverSeed(goal.id);
  const coverLabel = goal.title.trim().slice(0, 2).toUpperCase();
  const coverImage = createCoverDataUri({
    accent: coverSeed % 2 === 0 ? palette.primary.main : palette.secondary.main,
    background:
      palette.surface?.primarySoft ??
      palette.action.hover,
    label: coverLabel,
    muted:
      palette.surface?.softStrong ??
      palette.divider,
    seed: coverSeed,
  });

  return (
    <InitiativeCardRoot>
      <InitiativeCardActionArea component={RouterLink} to={`/goals/${goal.id}`}>
        <CoverMedia
          component="img"
          image={coverImage}
          alt={goal.title}
        />
        <InitiativeContent>
          <InitiativeTitle variant="subtitle1">{goal.title}</InitiativeTitle>
          <InitiativeDescription variant="body2" color="text.secondary">
            {goal.description}
          </InitiativeDescription>
          {popularitySummary ? (
            <Typography variant="caption" color="text.secondary">
              {popularitySummary}
            </Typography>
          ) : null}
        </InitiativeContent>
      </InitiativeCardActionArea>
    </InitiativeCardRoot>
  );
}
